import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";
import { Plus, Edit, Trash2, Music, Users, Crown, Search, X, Footprints, ClipboardCheck, UserPlus, Clock, CheckCircle, ChevronRight, Check, UserMinus, Settings2, HeartHandshake, Baby } from "lucide-react";
import { Checkbox } from "../components/ui/checkbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MinistriesPage() {
  const { isAdmin, currentBranch } = useAuth();
  const [ministries, setMinistries] = useState([]);
  const [people, setPeople] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMinistry, setEditingMinistry] = useState(null);
  const [form, setForm] = useState({ nome: "", descricao: "", lideres: [], tipo_louvor: false, conta_conexao: false, infantil: false, precisa_audicao: false, projeto_audicao_id: "", etapa_trilho_obrigatoria: "" });
  const [leaderSearch, setLeaderSearch] = useState("");
  const [trilhoEtapas, setTrilhoEtapas] = useState([]);
  const [pendingMembers, setPendingMembers] = useState([]);
  const [activeTab, setActiveTab] = useState("ministerios");
  const [selectedMinistry, setSelectedMinistry] = useState(null);
  const [ministryMembers, setMinistryMembers] = useState({ 
    ativos: [], 
    pendente_ativacao: [], 
    em_audicao: [], 
    interessados: [],
    aguardando_trilho: []
  });
  const [membersDialogOpen, setMembersDialogOpen] = useState(false);
  const [membersLoading, setMembersLoading] = useState(false);
  
  // States for functions modal
  const [functionsModalOpen, setFunctionsModalOpen] = useState(false);
  const [selectedMemberForFunctions, setSelectedMemberForFunctions] = useState(null);
  const [ministryFunctions, setMinistryFunctions] = useState([]);
  const [memberFunctions, setMemberFunctions] = useState([]);
  const [savingFunctions, setSavingFunctions] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [ministriesRes, peopleRes, etapasRes, projectsRes, pendingRes] = await Promise.all([
        axios.get(`${API}/ministries`),
        axios.get(`${API}/people`),
        axios.get(`${API}/trilho/etapas`, { params: { filial_id: currentBranch?.id } }).catch(() => ({ data: [] })),
        axios.get(`${API}/projects`).catch(() => ({ data: [] })),
        axios.get(`${API}/ministries/all-pending`).catch(() => ({ data: [] }))
      ]);
      setMinistries(ministriesRes.data);
      setPeople(peopleRes.data.filter(p => p.aprovado_funcao)); // Only approved volunteers
      setTrilhoEtapas(etapasRes.data || []);
      setProjects(projectsRes.data || []);
      setPendingMembers(pendingRes.data || []);
    } catch (error) {
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  const loadMinistryMembers = async (ministry) => {
    setSelectedMinistry(ministry);
    setMembersLoading(true);
    setMembersDialogOpen(true);
    try {
      const res = await axios.get(`${API}/ministries/${ministry.id}/all-members`);
      setMinistryMembers(res.data);
    } catch (error) {
      toast.error("Erro ao carregar membros");
      setMinistryMembers({ ativos: [], pendente_ativacao: [], em_audicao: [] });
    } finally {
      setMembersLoading(false);
    }
  };

  const handleActivateMember = async (ministryId, pessoaId) => {
    try {
      await axios.post(`${API}/ministries/${ministryId}/activate/${pessoaId}`);
      toast.success("Membro ativado com sucesso!");
      loadData();
      // Reload members if dialog is open
      if (selectedMinistry && selectedMinistry.id === ministryId) {
        loadMinistryMembers(selectedMinistry);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao ativar membro");
    }
  };

  const handleApproveAudition = async (ministryId, pessoaId) => {
    try {
      await axios.post(`${API}/ministries/${ministryId}/move-to-pending/${pessoaId}`);
      toast.success("Audição aprovada! Membro aguardando ativação.");
      loadData();
      // Reload members if dialog is open
      if (selectedMinistry && selectedMinistry.id === ministryId) {
        loadMinistryMembers(selectedMinistry);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao aprovar audição");
    }
  };

  const handleDeactivateMember = async (ministryId, pessoaId) => {
    if (!window.confirm("Deseja realmente desativar este membro do ministério? Ele não poderá mais ser escalado até ser reativado.")) {
      return;
    }
    try {
      await axios.post(`${API}/ministries/${ministryId}/deactivate-member/${pessoaId}`);
      toast.success("Membro desativado com sucesso!");
      loadData();
      // Reload members if dialog is open
      if (selectedMinistry && selectedMinistry.id === ministryId) {
        loadMinistryMembers(selectedMinistry);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao desativar membro");
    }
  };

  // Open functions modal for a member
  const handleOpenFunctionsModal = async (member, ministryId) => {
    setSelectedMemberForFunctions(member);
    setFunctionsModalOpen(true);
    
    try {
      // Load functions specific to this ministry (functions that have this ministry in ministerios_ids)
      const functionsRes = await axios.get(`${API}/ministries/${ministryId}/functions`);
      const ministryFuncs = functionsRes.data || [];
      setMinistryFunctions(ministryFuncs);
      
      // Get person's data to see current enabled functions
      const personRes = await axios.get(`${API}/people/${member.pessoa_id}`);
      const person = personRes.data;
      
      // Get current enabled functions for this person in this ministry
      const interesse = person?.ministerios_interesse?.find(i => i.ministerio_id === ministryId);
      
      // Priority: 
      // 1. Use funcoes_habilitadas if it exists AND has values
      // 2. Otherwise, use funcoes_desejadas from person's profile (filtered to ministry functions)
      // 3. If neither exists, start empty
      const funcHab = interesse?.funcoes_habilitadas;
      let enabledFunctions = [];
      
      if (funcHab && Array.isArray(funcHab) && funcHab.length > 0) {
        // Use saved funcoes_habilitadas
        enabledFunctions = funcHab;
      } else {
        // Fallback to funcoes_desejadas from person's profile
        // Filter to only include functions that belong to this ministry
        const funcDesejadas = person?.funcoes_desejadas || [];
        const ministryFuncIds = ministryFuncs.map(f => f.id);
        enabledFunctions = funcDesejadas.filter(fid => ministryFuncIds.includes(fid));
      }
      
      setMemberFunctions(enabledFunctions);
    } catch (error) {
      console.error("Error loading functions:", error);
      toast.error("Erro ao carregar funções");
    }
  };

  // Toggle a function for the member
  const handleToggleFunction = (functionId) => {
    setMemberFunctions(prev => {
      if (prev.includes(functionId)) {
        return prev.filter(id => id !== functionId);
      } else {
        return [...prev, functionId];
      }
    });
  };

  // Save member functions
  const handleSaveMemberFunctions = async () => {
    if (!selectedMemberForFunctions || !selectedMinistry) return;
    
    setSavingFunctions(true);
    try {
      await axios.post(`${API}/ministries/${selectedMinistry.id}/member-functions/${selectedMemberForFunctions.pessoa_id}`, {
        funcoes_habilitadas: memberFunctions
      });
      toast.success("Funções atualizadas com sucesso!");
      setFunctionsModalOpen(false);
      loadMinistryMembers(selectedMinistry);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao salvar funções");
    } finally {
      setSavingFunctions(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Build payload with backwards compatibility
      const payload = {
        ...form,
        filial_id: currentBranch?.id || null,
        // Keep lider_id and lider_nome for backwards compatibility (first leader)
        lider_id: form.lideres.length > 0 ? form.lideres[0].id : null,
        lider_nome: form.lideres.length > 0 ? form.lideres[0].nome : null
      };
      
      if (editingMinistry) {
        await axios.put(`${API}/ministries/${editingMinistry.id}`, payload);
        toast.success("Ministério atualizado!");
      } else {
        await axios.post(`${API}/ministries`, payload);
        toast.success("Ministério criado!");
      }
      setDialogOpen(false);
      setEditingMinistry(null);
      setForm({ nome: "", descricao: "", lideres: [], tipo_louvor: false, conta_conexao: false, infantil: false, precisa_audicao: false, projeto_audicao_id: "", etapa_trilho_obrigatoria: "" });
      setLeaderSearch("");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao salvar");
    }
  };

  const handleEdit = (ministry) => {
    setEditingMinistry(ministry);
    // Convert old format (lider_id/lider_nome) to new format (lideres array)
    let lideres = ministry.lideres || [];
    if (lideres.length === 0 && ministry.lider_id && ministry.lider_nome) {
      lideres = [{ id: ministry.lider_id, nome: ministry.lider_nome }];
    }
    setForm({
      nome: ministry.nome,
      descricao: ministry.descricao || "",
      lideres: lideres,
      tipo_louvor: ministry.tipo_louvor || false,
      infantil: ministry.infantil || false,
      conta_conexao: ministry.conta_conexao || false,
      precisa_audicao: ministry.precisa_audicao || false,
      projeto_audicao_id: ministry.projeto_audicao_id || "",
      etapa_trilho_obrigatoria: ministry.etapa_trilho_obrigatoria || ""
    });
    setLeaderSearch("");
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Deseja remover este ministério?")) return;
    try {
      await axios.delete(`${API}/ministries/${id}`);
      toast.success("Ministério removido");
      loadData();
    } catch (error) {
      toast.error("Erro ao remover");
    }
  };

  const toggleLeader = (person) => {
    const isSelected = form.lideres.some(l => l.id === person.id);
    if (isSelected) {
      setForm({
        ...form,
        lideres: form.lideres.filter(l => l.id !== person.id)
      });
    } else {
      setForm({
        ...form,
        lideres: [...form.lideres, { id: person.id, nome: person.nome_completo }]
      });
    }
  };

  const removeLeader = (leaderId) => {
    setForm({
      ...form,
      lideres: form.lideres.filter(l => l.id !== leaderId)
    });
  };

  const filteredPeople = people.filter(p =>
    p.nome_completo.toLowerCase().includes(leaderSearch.toLowerCase())
  );

  // Get leader names for display
  const getLeaderNames = (ministry) => {
    if (ministry.lideres && ministry.lideres.length > 0) {
      return ministry.lideres.map(l => l.nome).join(", ");
    }
    return ministry.lider_nome || null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="ministerios-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Ministérios</h1>
        {isAdmin() && (
          <Button onClick={() => { setEditingMinistry(null); setForm({ nome: "", descricao: "", lideres: [], tipo_louvor: false, conta_conexao: false, infantil: false, precisa_audicao: false, projeto_audicao_id: "", etapa_trilho_obrigatoria: "" }); setLeaderSearch(""); setDialogOpen(true); }} data-testid="add-ministry-button">
            <Plus className="h-4 w-4 mr-2" />
            Adicionar
          </Button>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 mt-4">
        {ministries.length === 0 ? (
          <p className="text-muted-foreground col-span-full text-center py-8">Nenhum ministério cadastrado</p>
        ) : (
          ministries.map((ministry) => (
            <Card key={ministry.id} className="card-hover cursor-pointer" data-testid={`ministry-card-${ministry.id}`} onClick={() => loadMinistryMembers(ministry)}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-3 rounded-xl ${ministry.tipo_louvor ? "bg-purple-500/10" : "bg-primary/10"}`}>
                          {ministry.tipo_louvor ? (
                            <Music className="h-5 w-5 text-purple-500" />
                          ) : (
                            <Users className="h-5 w-5 text-primary" />
                          )}
                        </div>
                        <div>
                          <h3 className="font-semibold">{ministry.nome}</h3>
                          {getLeaderNames(ministry) && (
                            <p className="text-sm text-muted-foreground flex items-center gap-1">
                              <Crown className="h-3 w-3" />
                              {(ministry.lideres?.length > 1) ? "Líderes: " : "Líder: "}{getLeaderNames(ministry)}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      {isAdmin() && (
                        <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                          <Button size="icon" variant="ghost" onClick={() => handleEdit(ministry)} data-testid={`edit-ministry-${ministry.id}`}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button size="icon" variant="ghost" className="text-destructive" onClick={() => handleDelete(ministry.id)} data-testid={`delete-ministry-${ministry.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                    
                    {ministry.descricao && (
                      <p className="text-sm text-muted-foreground mt-3">{ministry.descricao}</p>
                    )}
                    
                    <div className="flex flex-wrap gap-2 mt-3">
                      {ministry.tipo_louvor && (
                        <Badge className="bg-purple-500/10 text-purple-600">
                          <Music className="h-3 w-3 mr-1" />
                          Louvor
                        </Badge>
                      )}
                      {ministry.precisa_audicao && (
                        <Badge variant="outline" className="text-orange-600 border-orange-300">
                          <ClipboardCheck className="h-3 w-3 mr-1" />
                          Audição
                        </Badge>
                      )}
                    </div>
                    
                    <div className="flex items-center justify-between mt-3 pt-3 border-t">
                      <span className="text-xs text-muted-foreground">Clique para ver membros</span>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>{editingMinistry ? "Editar Ministério" : "Novo Ministério"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto space-y-4 py-4 pr-2">
            <div className="space-y-2">
              <Label>Nome *</Label>
              <Input
                required
                value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
                data-testid="ministry-nome"
              />
            </div>

            <div className="space-y-2">
              <Label>Descrição</Label>
              <Input
                value={form.descricao || ""}
                onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                data-testid="ministry-descricao"
              />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Crown className="h-4 w-4" />
                Líderes
              </Label>
              
              {/* Selected leaders */}
              {form.lideres.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {form.lideres.map((lider) => (
                    <Badge key={lider.id} variant="secondary" className="gap-1 pr-1">
                      {lider.nome}
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-4 w-4 ml-1 hover:bg-destructive/20"
                        onClick={() => removeLeader(lider.id)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </Badge>
                  ))}
                </div>
              )}

              {/* Search and select leaders */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar pessoa..."
                  value={leaderSearch}
                  onChange={(e) => setLeaderSearch(e.target.value)}
                  className="pl-10"
                  data-testid="ministry-lider-search"
                />
              </div>

              <ScrollArea className="h-40 border rounded-md">
                <div className="p-2 space-y-1">
                  {filteredPeople.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      {people.length === 0 ? "Nenhum voluntário aprovado" : "Nenhum resultado encontrado"}
                    </p>
                  ) : (
                    filteredPeople.map((person) => {
                      const isSelected = form.lideres.some(l => l.id === person.id);
                      return (
                        <label
                          key={person.id}
                          className={`flex items-center gap-2 p-2 rounded-md cursor-pointer hover:bg-muted ${isSelected ? 'bg-muted' : ''}`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleLeader(person)}
                            className="h-4 w-4 rounded border-primary text-primary focus:ring-primary"
                          />
                          <span className="text-sm">{person.nome_completo}</span>
                        </label>
                      );
                    })
                  )}
                </div>
              </ScrollArea>
            </div>

            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
              <div className="flex items-center gap-2">
                <Music className="h-4 w-4 text-purple-500" />
                <Label htmlFor="tipo_louvor" className="cursor-pointer">Ministério de Louvor</Label>
              </div>
              <Switch
                id="tipo_louvor"
                checked={form.tipo_louvor}
                onCheckedChange={(checked) => setForm({ ...form, tipo_louvor: checked })}
                data-testid="ministry-tipo-louvor"
              />
            </div>

            <div className="flex items-center justify-between p-3 rounded-lg bg-green-50 border border-green-100">
              <div className="flex items-center gap-2">
                <HeartHandshake className="h-4 w-4 text-green-600" />
                <div>
                  <Label htmlFor="conta_conexao" className="cursor-pointer">Conta como conexão</Label>
                  <p className="text-[11px] text-muted-foreground">Membros aprovados ficam conectados aos líderes (Igreja Conectada)</p>
                </div>
              </div>
              <Switch
                id="conta_conexao"
                checked={form.conta_conexao}
                onCheckedChange={(checked) => setForm({ ...form, conta_conexao: checked })}
                data-testid="ministry-conta-conexao"
              />
            </div>

            <div className="flex items-center justify-between p-3 rounded-lg bg-sky-50 border border-sky-100">
              <div className="flex items-center gap-2">
                <Baby className="h-4 w-4 text-sky-600" />
                <div>
                  <Label htmlFor="infantil" className="cursor-pointer">Ministério Infantil</Label>
                  <p className="text-[11px] text-muted-foreground">Crianças até 11 anos entram automaticamente e ficam conectadas aos líderes</p>
                </div>
              </div>
              <Switch
                id="infantil"
                checked={form.infantil}
                onCheckedChange={(checked) => setForm({ ...form, infantil: checked })}
                data-testid="ministry-infantil"
              />
            </div>

            {/* Trilho do Reino - Configurações */}
            <div className="space-y-3 p-3 rounded-lg bg-purple-50 border border-purple-100">
              <p className="text-sm font-medium text-purple-700 flex items-center gap-2">
                <Footprints className="h-4 w-4" />
                Trilho do Reino
              </p>
              
              <div className="flex items-center justify-between">
                <Label htmlFor="precisa_audicao" className="cursor-pointer text-sm">
                  Precisa de Audição/Aprovação
                </Label>
                <Switch
                  id="precisa_audicao"
                  checked={form.precisa_audicao}
                  onCheckedChange={(checked) => setForm({ ...form, precisa_audicao: checked, projeto_audicao_id: checked ? form.projeto_audicao_id : "" })}
                  data-testid="ministry-precisa-audicao"
                />
              </div>

              {/* Projeto de Audição - só aparece se precisa_audicao está ativo */}
              {form.precisa_audicao && (
                <div className="space-y-1">
                  <Label htmlFor="projeto_audicao" className="text-sm flex items-center gap-2">
                    <ClipboardCheck className="h-4 w-4" />
                    Projeto de Audição *
                  </Label>
                  <Select
                    value={form.projeto_audicao_id || "none"}
                    onValueChange={(value) => setForm({ ...form, projeto_audicao_id: value === "none" ? "" : value })}
                  >
                    <SelectTrigger data-testid="ministry-projeto-audicao">
                      <SelectValue placeholder="Selecione o projeto de audição" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Selecione um projeto</SelectItem>
                      {projects.map(projeto => (
                        <SelectItem key={projeto.id} value={projeto.id}>
                          {projeto.nome}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Quando alguém se cadastrar neste ministério, será inscrito automaticamente neste projeto para avaliação
                  </p>
                </div>
              )}

              {trilhoEtapas.length > 0 && (
                <div className="space-y-1">
                  <Label htmlFor="etapa_trilho" className="text-sm">
                    Etapa obrigatória do Trilho
                  </Label>
                  <Select
                    value={form.etapa_trilho_obrigatoria || "none"}
                    onValueChange={(value) => setForm({ ...form, etapa_trilho_obrigatoria: value === "none" ? "" : value })}
                  >
                    <SelectTrigger data-testid="ministry-etapa-trilho">
                      <SelectValue placeholder="Nenhuma etapa obrigatória" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nenhuma etapa obrigatória</SelectItem>
                      {trilhoEtapas.map(etapa => (
                        <SelectItem key={etapa.id} value={etapa.id}>
                          {etapa.nome}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Pessoa só poderá ser escalada após completar esta etapa
                  </p>
                </div>
              )}
            </div>

            <div className="flex gap-2 pt-4">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="flex-1" data-testid="save-ministry-button">
                Salvar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog de Membros do Ministério */}
      <Dialog open={membersDialogOpen} onOpenChange={setMembersDialogOpen}>
        <DialogContent className="max-w-2xl h-[85vh] flex flex-col p-0">
          <DialogHeader className="p-6 pb-2">
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              {selectedMinistry?.nome}
            </DialogTitle>
          </DialogHeader>
          
          {membersLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <Tabs defaultValue="ativos" className="flex-1 flex flex-col min-h-0 px-6 pb-6">
              <TabsList className="grid w-full grid-cols-4 text-xs shrink-0">
                <TabsTrigger value="ativos" className="gap-1 text-[10px] px-1">
                  <Check className="h-3 w-3" />
                  Ativos ({ministryMembers.ativos?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="aguardando" className="gap-1 text-[10px] px-1">
                  <Clock className="h-3 w-3" />
                  Aguard. ({ministryMembers.pendente_ativacao?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="audicao" className="gap-1 text-[10px] px-1">
                  <Music className="h-3 w-3" />
                  Audição ({ministryMembers.em_audicao?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="interesse" className="gap-1 text-[10px] px-1">
                  <Users className="h-3 w-3" />
                  Interesse ({ministryMembers.interessados?.length || 0})
                </TabsTrigger>
              </TabsList>

              <div className="flex-1 min-h-0 mt-4 overflow-y-auto">
                <TabsContent value="ativos" className="m-0">
                  {ministryMembers.ativos?.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">Nenhum membro ativo</p>
                  ) : (
                    <div className="space-y-2">
                      {ministryMembers.ativos?.map((member) => (
                        <div key={member.pessoa_id} className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-900">
                          <div className="flex items-center gap-3">
                            {member.foto_url ? (
                              <img src={member.foto_url} alt="" className="h-10 w-10 rounded-full object-cover" />
                            ) : (
                              <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                                <Users className="h-5 w-5 text-green-600" />
                              </div>
                            )}
                            <div>
                              <p className="font-medium">{member.nome}</p>
                              {member.telefone && <p className="text-sm text-muted-foreground">{member.telefone}</p>}
                              {member.funcoes_nomes && member.funcoes_nomes.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {member.funcoes_nomes.map((funcNome, idx) => (
                                    <Badge key={idx} variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                                      {funcNome}
                                    </Badge>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => handleOpenFunctionsModal(member, selectedMinistry?.id)}
                              className="text-blue-500 hover:text-blue-600 hover:bg-blue-50"
                              title="Gerenciar funções"
                              data-testid={`functions-member-${member.pessoa_id}`}
                            >
                              <Settings2 className="h-4 w-4" />
                            </Button>
                            <Badge className="bg-green-500">Ativo</Badge>
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => handleDeactivateMember(selectedMinistry?.id, member.pessoa_id)}
                              className="text-red-500 hover:text-red-600 hover:bg-red-50"
                              data-testid={`deactivate-member-${member.pessoa_id}`}
                            >
                              <UserMinus className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="aguardando" className="m-0">
                  {ministryMembers.pendente_ativacao?.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">Ninguém aguardando ativação</p>
                  ) : (
                    <div className="space-y-2">
                      {ministryMembers.pendente_ativacao?.map((member) => (
                        <div key={member.pessoa_id} className="flex items-center justify-between p-3 bg-orange-50 dark:bg-orange-950/20 rounded-lg border border-orange-200 dark:border-orange-900">
                          <div className="flex items-center gap-3">
                            {member.foto_url ? (
                              <img src={member.foto_url} alt="" className="h-10 w-10 rounded-full object-cover" />
                            ) : (
                              <div className="h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center">
                                <Users className="h-5 w-5 text-orange-600" />
                              </div>
                            )}
                            <div>
                              <p className="font-medium">{member.nome}</p>
                              {member.telefone && <p className="text-sm text-muted-foreground">{member.telefone}</p>}
                              {!member.tem_etapa_voluntario && (
                                <p className="text-xs text-red-500">Aguardando trilho voluntário</p>
                              )}
                            </div>
                          </div>
                          <Button 
                            size="sm" 
                            onClick={() => handleActivateMember(selectedMinistry?.id, member.pessoa_id)}
                            disabled={!member.tem_etapa_voluntario}
                            title={!member.tem_etapa_voluntario ? "Pessoa precisa completar o trilho de voluntário primeiro" : "Ativar membro"}
                            data-testid={`activate-member-${member.pessoa_id}`}
                          >
                            <Check className="h-4 w-4 mr-1" />
                            Ativar
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="audicao" className="m-0">
                  {ministryMembers.em_audicao?.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">Ninguém em audição</p>
                  ) : (
                    <div className="space-y-2">
                      <div className="p-3 bg-amber-50 dark:bg-amber-950/20 rounded-lg border border-amber-200 mb-4">
                        <p className="text-sm text-amber-800 dark:text-amber-200">
                          <strong>Atenção:</strong> Para aprovar pessoas na audição, vá até <strong>Inscrições</strong> → <strong>Projeto de Audição</strong> e aprove o participante lá. Após aprovação, a pessoa aparecerá em "Aguardando Ativação".
                        </p>
                      </div>
                      {ministryMembers.em_audicao?.map((member) => (
                        <div key={member.pessoa_id} className="flex items-center justify-between p-3 bg-purple-50 dark:bg-purple-950/20 rounded-lg border border-purple-200 dark:border-purple-900">
                          <div className="flex items-center gap-3">
                            {member.foto_url ? (
                              <img src={member.foto_url} alt="" className="h-10 w-10 rounded-full object-cover" />
                            ) : (
                              <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                                <Music className="h-5 w-5 text-purple-600" />
                              </div>
                            )}
                            <div>
                              <p className="font-medium">{member.nome}</p>
                              {member.telefone && <p className="text-sm text-muted-foreground">{member.telefone}</p>}
                              {member.tem_etapa_voluntario ? (
                                <Badge variant="outline" className="text-xs text-green-600 border-green-600 mt-1">
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  Trilho Completo
                                </Badge>
                              ) : (
                                <span className="text-xs text-amber-600 mt-1">Aguardando trilho voluntário</span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => handleOpenFunctionsModal(member, selectedMinistry?.id)}
                              className="text-blue-500 hover:text-blue-600 hover:bg-blue-50"
                              title="Gerenciar funções"
                              data-testid={`functions-audicao-${member.pessoa_id}`}
                            >
                              <Settings2 className="h-4 w-4" />
                            </Button>
                            <Badge className="bg-purple-500">Em Audição</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="interesse" className="m-0">
                  {ministryMembers.interessados?.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">Ninguém interessado ainda</p>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-sm text-muted-foreground mb-4">
                        Estas pessoas selecionaram este ministério mas ainda não completaram a etapa "Voluntário" do Trilho do Reino.
                      </p>
                      {ministryMembers.interessados?.map((member) => (
                        <div key={member.pessoa_id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-950/20 rounded-lg border border-gray-200 dark:border-gray-800">
                          <div className="flex items-center gap-3">
                            {member.foto_url ? (
                              <img src={member.foto_url} alt="" className="h-10 w-10 rounded-full object-cover" />
                            ) : (
                              <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center">
                                <Users className="h-5 w-5 text-gray-600" />
                              </div>
                            )}
                            <div>
                              <p className="font-medium">{member.nome}</p>
                              {member.telefone && <p className="text-sm text-muted-foreground">{member.telefone}</p>}
                            </div>
                          </div>
                          <Badge variant="outline">Interessado</Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>
              </div>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* Functions Management Modal */}
      <Dialog open={functionsModalOpen} onOpenChange={setFunctionsModalOpen}>
        <DialogContent className="max-w-md max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings2 className="h-5 w-5 text-blue-600" />
              Funções Habilitadas
            </DialogTitle>
            <DialogDescription>
              {selectedMemberForFunctions && (
                <span>Selecione as funções que <strong>{selectedMemberForFunctions.nome}</strong> pode exercer no ministério <strong>{selectedMinistry?.nome}</strong></span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-2 py-4 min-h-0">
            {ministryFunctions.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground">
                  Este ministério não tem funções vinculadas.
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  Vincule funções a este ministério no cadastro de Funções.
                </p>
              </div>
            ) : (
              ministryFunctions.map((func) => (
                <div 
                  key={func.id} 
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    memberFunctions.includes(func.id) 
                      ? 'bg-blue-50 border-blue-200 dark:bg-blue-950/20' 
                      : 'bg-gray-50 border-gray-200 dark:bg-gray-950/20'
                  }`}
                  onClick={() => handleToggleFunction(func.id)}
                >
                  <Checkbox
                    checked={memberFunctions.includes(func.id)}
                    onCheckedChange={() => handleToggleFunction(func.id)}
                    id={`func-${func.id}`}
                  />
                  <div className="flex-1">
                    <label htmlFor={`func-${func.id}`} className="font-medium cursor-pointer">
                      {func.nome}
                    </label>
                    {func.descricao && (
                      <p className="text-sm text-muted-foreground">{func.descricao}</p>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          <DialogFooter className="shrink-0">
            <Button variant="outline" onClick={() => setFunctionsModalOpen(false)}>
              Cancelar
            </Button>
            {ministryFunctions.length > 0 && (
              <Button onClick={handleSaveMemberFunctions} disabled={savingFunctions}>
                {savingFunctions ? "Salvando..." : "Salvar"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
